<template>
  <div class="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4">
    <div class="bg-white rounded-[25px] p-6 w-full max-w-lg relative shadow-xl max-h-[90vh] overflow-y-auto">
      <!-- Close Button -->
      <button
        class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
        @click="$emit('close')"
      >
        <svg
          viewBox="0 0 14 14"
          fill="none"
          class="w-[12px] h-[12px] text-gray-500"
        >
          <path
            d="M1.5 1.5L12.5 12.5M1.5 12.5L12.5 1.5"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <h3 class="text-xl font-['Raleway'] font-bold text-[#043f42] mb-1">
        Instantiate Cohort
      </h3>
      <p class="text-xs font-['Raleway'] text-gray-500 mb-5">
        Template: <strong>{{ template.name }}</strong> ({{ template.seta_type }})
      </p>

      <form @submit.prevent="submitInstantiate">
        <!-- Cohort Name -->
        <div class="mb-4">
          <label class="block text-xs font-bold text-[#043f42] font-['Raleway'] mb-1">
            New Cohort Name <span class="text-red-500">*</span>
          </label>
          <input
            v-model="cohortName"
            type="text"
            required
            placeholder="e.g. 2026 Q4 Intake"
            class="w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#01d680] font-['Raleway'] text-sm"
          >
        </div>

        <!-- Dates Row -->
        <div class="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label class="block text-xs font-bold text-[#043f42] font-['Raleway'] mb-1">
              Start Date <span class="text-red-500">*</span>
            </label>
            <input
              v-model="startDate"
              type="date"
              required
              class="w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#01d680] font-['Raleway'] text-sm"
            >
          </div>
          <div>
            <label class="block text-xs font-bold text-[#043f42] font-['Raleway'] mb-1">
              End Date <span class="text-red-500">*</span>
            </label>
            <input
              v-model="endDate"
              type="date"
              required
              class="w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#01d680] font-['Raleway'] text-sm"
            >
          </div>
        </div>

        <!-- Location -->
        <div class="mb-4">
          <label class="block text-xs font-bold text-[#043f42] font-['Raleway'] mb-1">
            Location <span class="text-red-500">*</span>
          </label>
          <input
            v-model="location"
            type="text"
            required
            placeholder="e.g. Johannesburg Campus / Remote"
            class="w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#01d680] font-['Raleway'] text-sm"
          >
        </div>

        <!-- Student CSV Dropzone -->
        <div class="mb-5">
          <label class="block text-xs font-bold text-[#043f42] font-['Raleway'] mb-1">
            Learner Enrollment CSV <span class="text-red-500">*</span>
          </label>
          <div
            class="border-2 border-dashed rounded-xl p-4 text-center transition-colors cursor-pointer"
            :class="csvErrorHighlight ? 'border-red-400 bg-red-50/40' : 'border-gray-300 hover:border-[#01d680] bg-gray-50/50'"
            @click="$refs.fileInput.click()"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".csv"
              class="hidden"
              @change="onFileSelected"
            >
            <div v-if="!selectedFile">
              <svg
                class="mx-auto h-8 w-8 mb-1"
                :class="csvErrorHighlight ? 'text-red-400' : 'text-gray-400'"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <span class="text-xs text-gray-600 font-['Raleway']">
                Click to upload or drag learner CSV here <span class="text-red-500">*</span>
              </span>
              <p class="text-[11px] text-gray-400 font-['Raleway'] mt-1">
                Max 100 learners per intake (SLA Quota Limit)
              </p>
            </div>
            <div
              v-else
              class="flex items-center justify-between px-2"
            >
              <span class="text-xs font-semibold text-[#043f42] truncate max-w-[280px]">
                {{ selectedFile.name }}
              </span>
              <button
                type="button"
                class="text-xs text-red-500 hover:text-red-700 font-bold ml-2"
                @click.stop="removeFile"
              >
                Remove
              </button>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div
          v-if="error"
          class="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-['Raleway']"
        >
          {{ error }}
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end gap-3 mt-6">
          <button
            type="button"
            class="px-5 py-2 rounded-[38px] border border-gray-300 text-gray-700 font-['Raleway'] font-bold text-xs hover:bg-gray-50 transition-colors"
            @click="$emit('close')"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSubmitting"
            class="px-5 py-2 rounded-[38px] bg-[#01d680] text-[#043f42] font-['Raleway'] font-bold text-xs hover:bg-[#00c270] transition-colors cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <span
              v-if="isSubmitting"
              class="loading loading-spinner loading-xs"
            />
            {{ isSubmitting ? 'Instantiating...' : 'Instantiate Cohort' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InstantiateCohortModal',
  props: {
    template: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'instantiated'],
  data () {
    return {
      cohortName: `${this.template.name} Intake`,
      startDate: '',
      endDate: '',
      location: this.template.location || this.template.blueprint_data?.location || '',
      selectedFile: null,
      isSubmitting: false,
      error: null,
      csvErrorHighlight: false
    }
  },
  methods: {
    onFileSelected (e) {
      const files = e.target.files
      if (files && files[0]) {
        this.selectedFile = files[0]
        this.csvErrorHighlight = false
        if (this.error === 'Learner enrollment CSV is required.') {
          this.error = null
        }
      }
    },
    removeFile () {
      this.selectedFile = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    async submitInstantiate () {
      if (!this.cohortName || !this.cohortName.trim()) {
        this.error = 'Cohort name is required.'
        return
      }
      if (!this.startDate) {
        this.error = 'Start date is required.'
        return
      }
      if (!this.endDate) {
        this.error = 'End date is required.'
        return
      }
      if (new Date(this.endDate) < new Date(this.startDate)) {
        this.error = 'End date cannot be earlier than start date.'
        return
      }
      if (!this.location || !this.location.trim()) {
        this.error = 'Location is required.'
        return
      }
      if (!this.selectedFile) {
        this.error = 'Learner enrollment CSV is required.'
        this.csvErrorHighlight = true
        return
      }

      this.csvErrorHighlight = false
      this.isSubmitting = true
      this.error = null

      try {
        const formData = new FormData()
        formData.append('cohort[name]', this.cohortName.trim())
        formData.append('cohort[start_date]', this.startDate)
        formData.append('cohort[end_date]', this.endDate)
        formData.append('cohort[location]', this.location.trim())
        formData.append('student_csv', this.selectedFile)

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

        const response = await fetch(`/cohorts/templates/${this.template.id}/instantiate`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json'
          },
          body: formData
        })

        const data = await response.json()

        if (!response.ok) {
          if (response.status === 403) {
            const gateModal = document.getElementById('subscription_gate_modal')
            if (gateModal) {
              this.$emit('close')
              gateModal.showModal()
              return
            }
          }
          throw new Error(data.error || 'Failed to instantiate cohort')
        }

        if (data.redirect_url) {
          window.location.href = data.redirect_url
        } else {
          this.$emit('instantiated', data)
          this.$emit('close')
        }
      } catch (err) {
        this.error = err.message
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>
